import { useNavigate } from "react-router-dom";
import { ArrowRight, Target, Eye, ShieldCheck, Zap, Handshake } from "lucide-react";
import { Reveal } from "../components/common";
import { Seo, breadcrumbs } from "../components/Seo";

const BG = "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const TIMELINE = [
  { year: "Our Mission", text: "To make commercial finance simple, transparent and accessible for every UK business, matching the right funding to the right opportunity." },
  { year: "Our Approach", text: "Whole-of-market, technology-enabled and relationship-led. We compare across 165+ lenders so you don't have to." },
  { year: "Our Promise", text: "Honest, jargon-free advice. We work for you, not the lender, and we measure success by the funding we secure for our clients." },
];

const VALUES = [
  { icon: ShieldCheck, title: "Trust", text: "Regulated, AML-compliant and transparent at every step." },
  { icon: Zap, title: "Speed", text: "Fast decisions without cutting corners on quality." },
  { icon: Target, title: "Precision", text: "The right lender and structure for your exact need." },
  { icon: Handshake, title: "Partnership", text: "A dedicated adviser who knows your business." },
];

export default function About() {
  const navigate = useNavigate();
  return (
    <div data-testid="about-page" className="pt-32">
      <Seo title="About Us" path="/about" description="DNA Finance is a Swansea-based, UK-wide commercial finance brokerage. Learn how our whole-of-market approach and dedicated advisers secure the right funding for your business." jsonLd={breadcrumbs([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])} />
      <section className="container-x mx-auto max-w-[1400px] py-16">
        <Reveal className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-lime mb-4">About DNA Finance</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">Commercial finance, in our DNA</h1>
          <p className="text-grey text-lg mt-6 leading-relaxed">DNA Finance is a UK commercial finance brokerage built on a simple belief: businesses deserve clear advice and genuine access to the whole market. We combine deep lender relationships with a modern, efficient process.</p>
          <p className="text-grey text-lg mt-4 leading-relaxed">Based in <span className="text-white font-semibold">South Wales</span>, our team brings a <span className="text-white font-semibold">combined 18+ years' experience</span> across the sales and finance industry, giving our clients the confidence of working with genuine specialists.</p>
        </Reveal>
      </section>

      <section className="container-x mx-auto max-w-[1400px] pb-16">
        <div className="relative h-[360px] md:h-[480px] border border-[#808080]/30 overflow-hidden">
          <img src={BG} alt="" className="w-full h-full object-cover grayscale" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent" />
        </div>
      </section>

      <section className="bg-black border-y border-[#808080]/20 py-24">
        <div className="container-x mx-auto max-w-[1400px]">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-16">What drives us</h2>
          <div className="relative border-l border-[#808080]/30 pl-8 md:pl-12 space-y-12">
            {TIMELINE.map((t, i) => (
              <Reveal key={t.year} delay={i * 0.1}>
                <div className="relative">
                  <span className="absolute -left-[42px] md:-left-[58px] top-1 w-4 h-4 bg-lime" />
                  <h3 className="text-xl font-bold text-lime">{t.year}</h3>
                  <p className="text-grey text-lg mt-3 max-w-2xl leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="container-x mx-auto max-w-[1400px] py-24">
        <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-16">Our values</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#808080]/20 border border-[#808080]/20">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="bg-[#1A1A1A] p-8 h-full">
                <v.icon className="w-9 h-9 text-lime mb-5" strokeWidth={1.5} />
                <h3 className="text-lg font-bold">{v.title}</h3>
                <p className="text-grey text-sm mt-3">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <button onClick={() => navigate("/brokers")} data-testid="about-cta-btn" className="mt-12 inline-flex items-center gap-2 bg-lime text-[#1A1A1A] font-bold px-8 py-4 hover:glow-lime transition-shadow">Work with us <ArrowRight className="w-5 h-5" /></button>
        </Reveal>
      </section>
    </div>
  );
}
